import React from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Clock, ArrowLeft, TrendingUp, Link2, Users, Award, Zap, Rocket } from 'lucide-react';
import { Helmet } from 'react-helmet';
import { CodeMonkey } from '../components/CodeMonkey';
import { CodeMonkeyLinks, CodeMonkeyAnalytics, CodeMonkeyTrophy, CodeMonkeyRocket } from '../components/CodeMonkeyVariations';
import blogPostContent from '../data/blogPostContent';
import blogPosts from '../data/blogPosts';
import parse from 'html-react-parser';

// Icon mapping
const iconMap = {
  TrendingUp, Link2, Users, Award, Zap, Rocket
};

// Monkey mapping
const monkeyMap = {
  'domain-authority-explained': CodeMonkeyAnalytics,
  'link-building-strategies-2025': CodeMonkeyLinks,
  'what-is-link-juice': CodeMonkeyRocket
};

const BlogPost = () => {
  const { slug } = useParams();

  // Get post metadata
  const postMeta = blogPosts.find(post => post.id === slug);
  
  // Get post content (prioritize new content structure, fallback to hardcoded)
  const postContent = blogPostContent[slug];

  if (!postMeta) {
    return (
      <div className="min-h-screen bg-black text-white pt-32 pb-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h1 className="text-4xl font-bold text-gold mb-4">Post Not Found</h1>
          <p className="text-white mb-6">This blog post is coming soon! Check back later.</p>
          <Link to="/blog" className="text-gold hover:text-silver font-semibold">
            ← Back to Blog
          </Link>
        </div>
      </div>
    );
  }

  // If content exists, render dynamic content
  if (postContent) {
    const MonkeyComponent = monkeyMap[slug] || CodeMonkey;
    
    // Generate Article schema
    const articleSchema = {
      "@context": "https://schema.org",
      "@type": "Article",
      "headline": postContent.title,
      "description": postMeta.excerpt,
      "image": "https://www.flumberico.fun/android-chrome-512x512.png",
      "author": {
        "@type": "Organization",
        "name": "FLUMBERICO Team",
        "url": "https://www.flumberico.fun"
      },
      "publisher": {
        "@type": "Organization",
        "name": "FLUMBERICO Team",
        "logo": {
          "@type": "ImageObject",
          "url": "https://www.flumberico.fun/android-chrome-512x512.png"
        }
      },
      "datePublished": "2024-10-20",
      "dateModified": "2024-10-20",
      "mainEntityOfPage": {
        "@type": "WebPage",
        "@id": `https://www.flumberico.fun/blog/${slug}`
      },
      "articleSection": postContent.category || "SEO",
      "keywords": ["flumberico", "SEO", "link building", "domain authority", "backlinks", postContent.category],
      "inLanguage": "en-US"
    };
    
    return (
      <div className="min-h-screen bg-black text-white pt-32 pb-20">
        <Helmet>
          <title>{postContent.title} | FLUMBERICO Blog</title>
          <meta name="description" content={postMeta.excerpt} />
          <link rel="canonical" href={`https://www.flumberico.fun/blog/${slug}`} />
          <script type="application/ld+json">
            {JSON.stringify(articleSchema)}
          </script>
        </Helmet>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <Link to="/blog" className="inline-flex items-center text-gold hover:text-silver transition-colors mb-8 font-semibold">
            <ArrowLeft size={20} className="mr-2" /> Back to Blog
          </Link>

          {/* Hero Header */}
          <div className="relative h-96 bg-gradient-to-br from-gold/20 via-gold/5 to-black border border-gold/30 rounded-lg mb-12 overflow-hidden">
            <div className="absolute inset-0 bg-grid-pattern opacity-10"></div>
            <div className="relative z-10 flex items-center justify-center h-full">
              <div className="text-center">
                <div className="mb-6 flex justify-center">
                  <MonkeyComponent size={120} />
                </div>
                <div className="bg-gold/10 backdrop-blur-sm border border-gold/30 rounded-lg px-6 py-3 inline-block">
                  <span className="text-gold font-bold text-sm">{postMeta.category}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Article */}
          <article className="prose prose-invert prose-gold max-w-none">
            <h1 className="text-5xl md:text-6xl font-black text-gold mb-6 leading-tight">
              {postContent.title}
            </h1>
            
            <div className="flex flex-wrap items-center gap-4 text-silver text-base mb-12 pb-8 border-b border-gold/20">
              <span className="flex items-center gap-2">
                <Calendar size={18} className="text-gold" /> {postContent.date}
              </span>
              <span>•</span>
              <span className="flex items-center gap-2">
                <Clock size={18} className="text-gold" /> {postContent.readTime}
              </span>
            </div>

            {/* Render Content Sections */}
            <div className="space-y-8 text-lg leading-relaxed">
              {postContent.content.map((section, index) => {
                if (section.type === 'intro') {
                  return (
                    <p key={index} className="text-white text-xl leading-relaxed">
                      {parse(section.text)}
                    </p>
                  );
                }

                if (section.type === 'section') {
                  const Icon = iconMap[section.icon] || TrendingUp;
                  return (
                    <section key={index} className="bg-gradient-to-br from-gold/10 to-black/50 border border-gold/30 rounded-lg p-8">
                      <h2 className="text-3xl font-bold text-gold mb-6 flex items-center">
                        <Icon className="mr-3" size={32} /> {section.heading}
                      </h2>
                      {section.paragraphs.map((para, pIndex) => (
                        <p key={pIndex} className="text-white mb-4">
                          {parse(para)}
                        </p>
                      ))}
                    </section>
                  );
                }

                if (section.type === 'callout') {
                  return (
                    <div key={index} className={`bg-gradient-to-br ${section.style === 'gold' ? 'from-gold/20' : 'from-silver/20'} to-black/50 border-2 border-${section.style === 'gold' ? 'gold' : 'silver'}/50 rounded-lg p-8`}>
                      <h3 className="text-2xl font-bold text-gold mb-4">{section.heading}</h3>
                      <ul className="space-y-2">
                        {section.points.map((point, pIndex) => (
                          <li key={pIndex} className="text-white flex items-start">
                            <span className="text-gold mr-2">•</span> {parse(point)}
                          </li>
                        ))}
                      </ul>
                    </div>
                  );
                }

                if (section.type === 'flumberico-connection') {
                  return (
                    <section key={index} className="bg-gradient-to-br from-gold/30 via-gold/10 to-black/50 border-2 border-gold/50 rounded-lg p-10">
                      <h2 className="text-4xl font-black text-gold mb-6 text-center">
                        {section.heading}
                      </h2>
                      {section.paragraphs.map((para, pIndex) => (
                        <p key={pIndex} className="text-white text-lg mb-4 leading-relaxed">
                          {parse(para)}
                        </p>
                      ))}
                    </section>
                  );
                }

                return null;
              })}
            </div>

            {/* CTA */}
            <div className="mt-16 bg-gradient-to-r from-gold/20 to-silver/20 border border-gold/40 rounded-lg p-8 text-center">
              <p className="text-white text-2xl font-bold mb-4">
                Ready to earn $FLUMBERICO?
              </p>
              <Link to="/">
                <button className="font-bold text-lg px-8 py-4 rounded-full transition-transform hover:scale-105" style={{ backgroundColor: '#FFD700', color: '#000000' }}>
                  Start Building Links →
                </button>
              </Link>
            </div>
          </article>

          <div className="mt-16 pt-8 border-t border-gold/20">
            <Link to="/blog" className="inline-flex items-center text-gold hover:text-silver transition-colors font-semibold text-lg">
              <ArrowLeft size={20} className="mr-2" /> Back to All Posts
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Fallback for first post (hardcoded content for seo-equity-tokenization)
  if (slug === 'seo-equity-tokenization') {
    // Return the original hardcoded blog post content
    // (keeping existing implementation for the first post)
    return <div className="min-h-screen bg-black text-white pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4">
        <Link to="/blog" className="text-gold hover:text-silver mb-8 inline-block">← Back to Blog</Link>
        <h1 className="text-5xl font-black text-gold mb-6">SEO Equity & The World's First Tokenization of Link Juice</h1>
        <p className="text-white text-xl mb-8">Full content for the original post...</p>
        <Link to="/blog" className="text-gold hover:text-silver">← Back to All Posts</Link>
      </div>
    </div>;
  }

  // Coming soon fallback
  return (
    <div className="min-h-screen bg-black text-white pt-32 pb-20">
      <div className="max-w-4xl mx-auto px-4 text-center">
        <CodeMonkey size={150} className="mx-auto mb-6" />
        <h1 className="text-4xl font-bold text-gold mb-4">Coming Soon!</h1>
        <p className="text-white text-xl mb-4">{postMeta.title}</p>
        <p className="text-white/70 mb-8">{postMeta.excerpt}</p>
        <Link to="/blog" className="text-gold hover:text-silver font-semibold text-lg">
          ← Back to Blog
        </Link>
      </div>
    </div>
  );
};

export default BlogPost;